import type { Session } from '@supabase/supabase-js';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@nuberu/ui';
import { useCallback, useEffect, useState, type ReactElement, type ReactNode } from 'react';

import { ApiError } from '@/lib/api';

import { fetchQueueHealth, retryJob, type QueueHealth } from './fetch-queues';

type RetryState = 'idle' | 'busy' | 'done' | 'error';

// Per-job retry control. Local state so each failed job's button is independent;
// on success it flips to a confirmation and refreshes the panel.
const RetryButton = ({
  session,
  jobId,
  onDone,
}: {
  session: Session;
  jobId: string;
  onDone: () => void;
}): ReactElement => {
  const [state, setState] = useState<RetryState>('idle');

  const click = async (): Promise<void> => {
    setState('busy');
    try {
      await retryJob(session, jobId);
      setState('done');
      onDone();
    } catch {
      setState('error');
    }
  };

  if (state === 'done') {
    return <span className="text-xs text-status-success">reencolado ✓</span>;
  }
  return (
    <Button variant="neutral" size="sm" disabled={state === 'busy'} onClick={() => void click()}>
      {state === 'busy' ? '…' : state === 'error' ? 'Reintentar ⚠' : 'Reintentar'}
    </Button>
  );
};

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: QueueHealth };

// Seconds → a compact age ("45s", "12m", "3h", "2d"); null when nothing pending.
const formatAge = (seconds: number | null): string => {
  if (seconds === null) return '—';
  if (seconds < 60) return `${String(seconds)}s`;
  if (seconds < 3600) return `${String(Math.floor(seconds / 60))}m`;
  if (seconds < 86_400) return `${String(Math.floor(seconds / 3600))}h`;
  return `${String(Math.floor(seconds / 86_400))}d`;
};

// ISO → "2026-08-15 10:30" (UTC as stored); "—" when absent.
const formatWhen = (iso: string | null): string =>
  iso === null ? '—' : iso.slice(0, 16).replace('T', ' ');

const Th = ({ children, right }: { children: ReactNode; right?: boolean }): ReactElement => (
  <th
    className={`pb-2 text-xs font-medium uppercase tracking-wide text-text-secondary ${
      right ? 'text-right' : 'text-left'
    }`}
  >
    {children}
  </th>
);

const Td = ({ children, right }: { children: ReactNode; right?: boolean }): ReactElement => (
  <td className={`py-2 text-sm text-text-primary ${right ? 'text-right tabular-nums' : ''}`}>
    {children}
  </td>
);

export const QueuesPanel = ({ session }: { session: Session }): ReactElement => {
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  const load = useCallback(
    (signal?: AbortSignal): void => {
      setState({ status: 'loading' });
      void fetchQueueHealth(session, signal)
        .then((data) => {
          if (!signal?.aborted) setState({ status: 'ready', data });
        })
        .catch((err: unknown) => {
          if (signal?.aborted) return;
          const message =
            err instanceof ApiError
              ? err.status === 403
                ? 'Tu cuenta no tiene acceso al backoffice.'
                : err.message
              : 'No pudimos cargar el estado de las colas.';
          setState({ status: 'error', message });
        });
    },
    [session],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Colas de trabajo</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => load()}
            disabled={state.status === 'loading'}
          >
            {state.status === 'loading' ? 'Actualizando…' : 'Actualizar'}
          </Button>
        </CardHeader>
        <CardContent>
          {state.status === 'loading' ? (
            <p className="py-4 text-sm text-text-secondary">Cargando…</p>
          ) : state.status === 'error' ? (
            <p className="py-4 text-sm text-status-warning" role="alert">
              {state.message}
            </p>
          ) : state.data.queues.length === 0 ? (
            <p className="py-2 text-sm text-text-secondary">Sin colas registradas.</p>
          ) : (
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-[1.5px] border-(color:--nuberu-ink)">
                  <Th>Cola</Th>
                  <Th right>Pendientes</Th>
                  <Th right>Activos</Th>
                  <Th right>Fallidos</Th>
                  <Th right>Más antiguo</Th>
                </tr>
              </thead>
              <tbody>
                {state.data.queues.map((q) => (
                  <tr
                    key={q.name}
                    className="border-b border-(color:--nuberu-line-soft) last:border-b-0"
                  >
                    <Td>
                      <code className="font-mono text-xs">{q.name}</code>
                    </Td>
                    <Td right>{q.created + q.retry}</Td>
                    <Td right>{q.active}</Td>
                    <Td right>
                      {q.failed > 0 ? (
                        <Badge tone="warning">{q.failed}</Badge>
                      ) : (
                        <span className="text-text-secondary">0</span>
                      )}
                    </Td>
                    <Td right>{formatAge(q.oldestPendingAgeSeconds)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {state.status === 'ready' ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Fallos recientes</CardTitle>
            </CardHeader>
            <CardContent>
              {state.data.failedRecent.length === 0 ? (
                <p className="py-2 text-sm text-text-secondary">Sin fallos. 🎉</p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {state.data.failedRecent.map((f, i) => (
                    <li
                      key={`${f.name}:${String(i)}`}
                      className="border-b border-(color:--nuberu-line-soft) pb-2 last:border-b-0"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <code className="font-mono text-xs">{f.name}</code>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-text-secondary">
                            {formatWhen(f.failedAt)}
                          </span>
                          {f.retryable ? (
                            <RetryButton session={session} jobId={f.id} onDone={() => load()} />
                          ) : null}
                        </div>
                      </div>
                      {f.error !== null ? (
                        <p className="mt-0.5 text-xs text-status-warning">{f.error}</p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Tareas programadas</CardTitle>
            </CardHeader>
            <CardContent>
              {state.data.schedules.length === 0 ? (
                <p className="py-2 text-sm text-text-secondary">Sin tareas programadas.</p>
              ) : (
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-[1.5px] border-(color:--nuberu-ink)">
                      <Th>Tarea</Th>
                      <Th>Cron</Th>
                      <Th right>Última ejecución</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.data.schedules.map((s) => (
                      <tr
                        key={s.name}
                        className="border-b border-(color:--nuberu-line-soft) last:border-b-0"
                      >
                        <Td>
                          <code className="font-mono text-xs">{s.name}</code>
                        </Td>
                        <Td>
                          <code className="font-mono text-xs">{s.cron}</code>
                        </Td>
                        <Td right>{formatWhen(s.lastRunAt)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
};
